import React from "react";
import {
  Box,
  Card,
  CardActions,
  Typography,
  Stack,
  SxProps,
  CardProps,
  Grid,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { IDevice, PerformanceConfig } from "models/Device";
import HealthyChip from "component/commonComponents/HealthyChip";
import GenericDeviceImage from "../../../assets/images/generic_laptop.png";
import StatusChip from "./StatusChip";
import { NOT_APPLICABLE } from '../../../constants/originalState';
import UptimeImage from "component/commonComponents/UptimeImage/UptimeImage";

const deviceInfoLayouts = {
  label: { xs: 6, md: 5 },
  value: { xs: 6, md: 7 },
};

export type Props = {
  deviceInfo: IDevice;
  title: React.ReactNode;
  serialNumber?: string;
  performance?: string;
  actions: JSX.Element;
  direction?: "row" | "column";
  locale?: Record<string, any>;
  holderName?: string;
  cardConfig?: {
    isShowPrimaryLabel?: boolean;
    isShowSerialNumber?: boolean;
    isShowLastScanned?: boolean;
    isShowAssigned?: boolean;
  };
  image?: string;
  cardStyles?: SxProps;
  cardImageStyles?: SxProps;
  cardProps?: CardProps;
  performanceConfig: PerformanceConfig;
  virtualDeviceId?: string;
  virtualDeviceDesc?: string;
  noGap?: boolean;
  imagePaddingTop?: boolean;
  localePerformance?: any;
  label?: string
};

const ProductCard = ({
  deviceInfo,
  title,
  serialNumber,
  image,
  performance,
  actions,
  direction = "column",
  locale,
  holderName,
  cardConfig = {},
  performanceConfig,
  cardStyles = {},
  cardImageStyles = {},
  cardProps = {},
  virtualDeviceId,
  virtualDeviceDesc,
  noGap = false,
  imagePaddingTop = false,
  localePerformance=  {},
  label,
}: Props) => {
  const { isShowPrimaryLabel, isShowSerialNumber, isShowLastScanned, isShowAssigned } = cardConfig;

  const assetEyebrow = deviceInfo?.process_stage?.asset_eyebrow?.toUpperCase() || "";
  const status = deviceInfo?.process_stage?.stage?.toUpperCase();
  const assetStatus = deviceInfo?.asset_function?.toUpperCase();

  const lastScanned = deviceInfo?.configuration?.lastActive;

  return (
    <Card
      sx={{
        width: "100%",
        padding: "16px",
        maxWidth: { xs: "100%", sm: "45%", md: "45%", lg: 280 },
        borderRadius: "16px",
        display: "flex",
        justifyContent: direction === "row" ? "unset" : "space-between",
        alignItems: "center",
        flexDirection: direction,
        gap: "24px",
        boxShadow: `0px 0px 0px 1px ${grey[300]}`,
        ...cardStyles,
      }}
      data-device_sn={serialNumber}
      {...cardProps}
    >
      <UptimeImage
        src={image || GenericDeviceImage}
        alt="image card"
        title="Image Card"
        width="auto"
        style={{ height: "113px", ...cardImageStyles }}
        wrapperProps={{
          style: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: imagePaddingTop ? "48px" : "0px",
            objectFit: "contain",
            aspectRatio: "1",
            transitionDelay: "1s",
          },
        }}
      />
      <Stack gap={noGap ? "0px" : "16px"} width="100%">
        <Stack>
          {label && (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontSize: "12px" }}
            >
              {label}
            </Typography>
          )}
          <Tooltip title={title} arrow placement="bottom-start">
            <Typography
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
              variant="h5"
              color="text.primary"
              sx={{ fontSize: "20px", fontWeight: "600" }}
            >
              {title || NOT_APPLICABLE}
            </Typography>
          </Tooltip>
        </Stack>
        {assetEyebrow && (
          <StatusChip
            assetEyebrow={assetEyebrow}
            status={status}
            assetStatus={assetStatus}
            isShowPrimaryLabel={isShowPrimaryLabel}
            locale={locale}
          />
        )}

        {isShowSerialNumber && (
          <DeviceInfo label={locale?.serialNumber} value={serialNumber} />
        )}

        {isShowLastScanned && (
          <DeviceInfo label={locale?.lastScanned} value={lastScanned} />
        )}

        {performanceConfig?.isEnabledTachyon && performance && (
          <Grid container alignItems="center" columnSpacing={1}>
            <Grid item {...deviceInfoLayouts.label}>
              <Typography
                variant="overline"
                alignSelf="center"
                color="text.secondary"
                lineHeight="18px"
              >
                {(locale?.performance as string)?.toUpperCase()}
              </Typography>
            </Grid>

            <Grid item {...deviceInfoLayouts.value}>
              <Box>
                <HealthyChip
                  performance={performance}
                  performanceConfig={performanceConfig}
                  localeText={localePerformance}
                />
              </Box>
            </Grid>
          </Grid>
        )}

        <DeviceInfo label={locale?.description} value={virtualDeviceDesc} />

        {isShowAssigned && (
          <DeviceInfo
            label={locale?.assignedTo}
            value={holderName}
            hasTooltip
          />
        )}

        <CardActions
          sx={{
            padding: 0,
            justifyContent: noGap ? "flex-end" : "initial",
          }}
        >
          {actions}
        </CardActions>
      </Stack>
    </Card>
  );
};


const DeviceInfo = ({ label, value, hasTooltip }: { label: string; value?: string; hasTooltip?: boolean }) => {
  if (!value) return null;
  return (
    <Grid container alignItems="center" columnSpacing={1}>
      <Grid item {...deviceInfoLayouts.label}>
        <Typography variant="overline" alignSelf="center" color="text.secondary" lineHeight="18px">
          {label?.toUpperCase()}
        </Typography>
      </Grid>
      <Grid item {...deviceInfoLayouts.value}>
        <Tooltip title={hasTooltip ? value : ''} arrow placement="bottom-start">
          <Typography
            alignSelf="center"
            variant="body1"
            color="text.secondary"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            overflow="hidden"
            fontWeight={400}
            fontSize="14px"
            lineHeight="20px"
          >
            {value}
          </Typography>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

ProductCard.StatusChip = StatusChip;

export default ProductCard;
